import { useGetChallengeDetailsQuery } from "@configs/apis/GetChallengeDetails";
import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import CheckIcon from "../../assets/icons/Check";
import ChevronIcon from "../../assets/icons/Chevron";
import ClockIcon from "../../assets/icons/Clock";
import GiftVariantIcon from "../../assets/icons/GiftVariant";
import HistoryBoldIcon from "../../assets/icons/HistoryBold";
import LockIcon from "../../assets/icons/Lock";
import LockedIcon from "../../assets/icons/Locked";
import SpinnerIcon from "../../assets/icons/Spinner";
import UsersIcon from "../../assets/icons/Users";
import DetailsCard from "../../components/Cards/DetailsCard";
import Button from "../../components/Common/Button";
import ImageWithFallback from "../../components/Common/ImageWithFallback";
import Skeleton from "../../components/Common/Skeleton";
import Popup from "../../components/Popup";
import type { Progress as ProgressType } from "../../configs/apis/GetProgress/models";
import { useUpdateIsClaimedMutation } from "../../configs/apis/UpdateIsClaimed";
import { Operator, RewardStatusType } from "../../configs/models/constants";
import {
  formatRemainingTime,
  getActiveMilestonePrize,
  getLargestUncompleted,
  getRewardType,
} from "../../configs/utils/utility";
import GroupsDrawer from "./GroupsDrawer";

const ChallengeDetailsPage = () => {
  const { t: translate } = useTranslation("translation");
  const { challengeId } = useParams<{ challengeId: string }>();
  const navigate = useNavigate();

  const [isTriggersExpanded, setIsTriggersExpanded] = useState(false);

  const [isPopupVisible, setIsPopupVisible] = useState(false);

  const [updateIsClaimed, { isLoading: isClaiming, isSuccess: hasClaimed }] =
    useUpdateIsClaimedMutation();

  const {
    data: detailsData,
    isLoading,
    isFetching,
    refetch,
  } = useGetChallengeDetailsQuery(
    {
      challengeId: Number(challengeId),
    },
    { refetchOnMountOrArgChange: true },
  );

  const activeMilestonePrize = getActiveMilestonePrize(detailsData?.prizes);
  const prize = activeMilestonePrize?.prizes?.at(0) || detailsData?.rewards?.at(0);

  const prizeStatus = activeMilestonePrize?.prizes?.at(0)?.status;
  const isPendingOrProcessing =
    prizeStatus === RewardStatusType.PENDING ||
    prizeStatus === RewardStatusType.PROCESSING;

  if (isLoading || isFetching)
    return (
      <div className="flex min-h-[40rem] grid-cols-1 flex-col-reverse gap-8 pb-24 pt-5 md:grid md:grid-cols-2">
        <Skeleton className="h-full" />
        <Skeleton className="h-full" hasContent={false} />
      </div>
    );

  const checkCompleted = (): boolean | undefined => {
    let groupsResult: boolean[] | undefined = [];

    // loop on triggers
    groupsResult = detailsData?.groupProgress?.map((group) => {
      if (group.triggersProgress[0]?.operator === Operator.OR) {
        return group.triggersProgress.some(
          (trigger) => trigger.progress === 100,
        );
      }

      return group.triggersProgress.every(
        (trigger) => trigger.progress === 100,
      );
    });
    // loop on each group to get ultimate result
    return detailsData?.groupProgress.reduce((ultimate, currentGroup, idx) => {
      if (idx === detailsData.groupProgress.length - 1) return ultimate;

      return currentGroup?.operator === Operator.OR
        ? !!groupsResult?.some((value) => value === true)
        : !!groupsResult?.every((value) => value === true);
    }, groupsResult?.at(0));
  };

  const getHasMoreValue = (): boolean => {
    if (detailsData?.prizes.length !== 0) return false;

    if (detailsData.groupProgress.length > 1) return true;

    return !!detailsData?.groupProgress?.some((group) => {
      return group.triggersProgress.length > 1;
    });
  };

  if (!detailsData)
    return (
      <main className="flex min-h-[400px] flex-col items-center justify-center text-center">
        <HistoryBoldIcon className="text-muted" />
        <p className="mb-8 text-foreground-lighter">{translate("challenge_not_found")}</p>
        <Link className="text-primary underline" to="/">
          {translate("go_back")}
        </Link>
      </main>
    );

  return (
    <>
      <Popup
        isVisible={isPopupVisible}
        onClose={() => {
          setIsPopupVisible(false);
          updateIsClaimed({
            challengeId: detailsData?.id ?? 0,
          })
            .unwrap()
            .then(() => {
              refetch().then((data) => {
                if (!data.data) navigate("/");
              });
            })
            .catch(() => {});
        }}
        prize={{
          value: prize?.value ?? "",
          description: prize?.description ?? "",
          expirationDate: prize?.expirationDate ?? "",
          rewardType: prize?.rewardType ?? 0,
        }}
      />
      <main>
        <div>
          <h3 className="text-3xl font-semibold text-foreground/80">
            {translate("challenges_pager")}
          </h3>
        </div>
        <div className="flex grid-cols-1 flex-col-reverse gap-8 pb-24 pt-5 md:grid md:grid-cols-2">
          <div className="flex h-fit flex-col gap-4 rounded-xl border border-foreground-lighter/40 p-4">
            <h3 className="text-xl font-semibold">{detailsData?.name}</h3>
            <div className="">
              <h4 className="font-semibold">{translate("about_challenge")}</h4>
              <p className="text-foreground-lighter">
                {detailsData?.description}
              </p>
            </div>

            <div className="mb-4 grid w-full gap-4 text-sm md:grid-cols-2 lg:text-base">
              {detailsData?.rewards?.map((reward) => (
                <div
                  className="flex justify-between gap-2 whitespace-nowrap rounded-xl border border-foreground-lighter/40 p-4 md:justify-center lg:justify-between"
                  key={reward.id}
                >
                  <span className="flex items-center gap-2 text-primary">
                    <GiftVariantIcon className="text-primary" />
                    {translate("reward")}
                  </span>
                  <p className="font-bold">
                    {getRewardType(translate, reward.rewardType, reward.rewardValue)}
                  </p>
                </div>
              ))}
              <Button
                className="text-nowrap px-4"
                disabled={
                  isPendingOrProcessing ||
                  isClaiming ||
                  hasClaimed ||
                  detailsData?.prizes.length === 0
                }
                onClick={() => {
                  setIsPopupVisible(true);
                }}
                style={isPendingOrProcessing ? { backgroundColor: "#fde7b5", color: "#000", border: "2px solid #d4a843" } : undefined}
              >
                {isPendingOrProcessing ? (
                  <div aria-live="polite" aria-atomic="true">
                    <span className="inline-flex items-center gap-2">
                      <SpinnerIcon className="size-4 animate-spin motion-reduce:animate-none fill-foreground" />
                      {translate("pending")}
                    </span>
                  </div>
                ) : isClaiming ? (
                  <div aria-live="polite" aria-atomic="true">
                    <span className="inline-flex items-center gap-2">
                      <SpinnerIcon className="size-4 animate-spin motion-reduce:animate-none fill-foreground" />
                      {translate("processing")}
                    </span>
                  </div>
                ) : detailsData?.isLocked ? (
                  translate("in_progress_challenges")
                ) : (
                  translate("view_reward")
                )}
              </Button>
            </div>

            {!!detailsData?.lockedEvent && (
              <PendingSection
                isLocked={!!detailsData?.lockedEvent}
                lockedEvent={detailsData.lockedEvent}
              />
            )}

            {!detailsData?.isLocked && (
              <div className="relative flex flex-col gap-4 rounded-xl border border-foreground-lighter/40 p-4 pt-8">
                <div className="absolute -top-3 left-0 flex w-full justify-center">
                  <button
                    className={clsx(
                      getHasMoreValue()
                        ? "text-primary"
                        : "text-foreground-lighter/40",
                      "w-fit bg-background px-2 flex font-semibold items-center",
                    )}
                    disabled={!getHasMoreValue()}
                    onClick={() => setIsTriggersExpanded((prev) => !prev)}
                  >
                    {translate("complete_this_group")}
                    {getHasMoreValue() && (
                      <ChevronIcon
                        className={clsx(
                          isTriggersExpanded ? "rotate-90" : "-rotate-90",
                          "h-2/3",
                        )}
                      />
                    )}{" "}
                  </button>
                </div>
                {isTriggersExpanded && (
                  <GroupsDrawer
                    groupProgress={detailsData?.groupProgress || []}
                  />
                )}
                {!isTriggersExpanded && (
                  <DetailsCard
                    isCompleted={
                      checkCompleted() || detailsData?.prizes.length !== 0
                    }
                    isMoreThanOne={getHasMoreValue()}
                    trigger={getLargestUncompleted(
                      detailsData?.groupProgress || [],
                    )}
                  />
                )}
              </div>
            )}

            <div className="flex flex-col divide-y divide-foreground-lighter/40 rounded-xl border border-foreground-lighter/40 px-4">
              <div className="flex w-full items-center justify-between py-4">
                <span className="flex items-center gap-2 text-primary">
                  <UsersIcon className="text-primary" />
                  {translate("finish_this_challenge")}
                </span>
                <p className="font-bold">
                  {detailsData?.peopleFinished} {translate("player")}
                </p>
              </div>
            </div>
          </div>
          <div>
            <div className="relative aspect-square w-full overflow-hidden rounded-2xl">
              {detailsData?.isLocked && (
                <div className="absolute flex size-full items-center justify-center rounded-xl bg-black/45">
                  <LockedIcon />
                </div>
              )}
              <div className="absolute flex size-full flex-col justify-between p-5">
                <div className="flex justify-between">
                  <span className="z-[1] flex h-fit items-center gap-1 rounded-full bg-background/80 px-4 py-2 text-foreground-lighter">
                    <ClockIcon /> {formatRemainingTime(detailsData?.endDate, translate)}
                  </span>
                </div>
                {detailsData?.category && (
                  <span className="w-fit rounded-full bg-white/50 px-2 py-1 text-foreground">
                    {detailsData?.category?.name}
                  </span>
                )}
              </div>
              <ImageWithFallback
                alt={detailsData.name}
                className="size-full"
                src={detailsData?.image}
              />
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

type PendingSectionProps = {
  isLocked: boolean;
  lockedEvent: ProgressType;
};

export const PendingSection = ({
  isLocked,
  lockedEvent,
}: PendingSectionProps) => {
  const { t: translate } = useTranslation("translation");

  return (
    <div className="rounded-2xl bg-primary/15 px-6">
      <Link
        className="my-4 flex w-full items-center justify-between gap-4"
        to={`/gamification/details/${lockedEvent.id}`}
      >
        <div className="relative flex items-center justify-center">
          {!isLocked && (
            <div className="absolute z-10 rounded-full bg-success p-2">
              <CheckIcon className="size-3" />
            </div>
          )}
          <LockIcon className="text-primary"  />
        </div>
        <div className="w-full text-start">
          <p className="text-lg font-semibold text-primary">
            {translate("locked_title")}
          </p>
          <p>{`${translate("locked_desc")} "${lockedEvent.name}" `}</p>
        </div>
        <ChevronIcon className="size-8 text-primary ltr:rotate-180" />
      </Link>
    </div>
  );
};

export default ChallengeDetailsPage;
